<script setup lang="ts">
  // Vue core imports
  import { onMounted, ref, watch } from 'vue'

  // External library imports
  import * as yup from 'yup'

  import { useI18n } from 'vue-i18n'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'

  import CPForm from '@ui/components/CPForm.vue'

  import CPFormItem from '@ui/components/CPFormItem.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPText from '@ui/components/CPText.vue'

  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { Screens, type AuthForgotPasswordFormRef, type ScreenChangeData } from '../../types/auth'

  import { getErrorMessage } from '../../utils'

  import Recaptcha from '../captcha/ReCaptcha.vue'

  // Props definitions
  const props = defineProps({
    email: {
      type: String,
      default: '',
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: ScreenChangeData): void
    (e: 'loginCallback', value: Record<string, unknown>): void
  }>()

  // i18n
  const { t } = useI18n()

  // Refs and reactive variables
  const errorMessage = ref<string | null>(null)

  const loading = ref(false)

  const showRecaptcha = ref(false)

  const reCaptchaToken = ref<string | undefined>(undefined)

  const resetPasswordDisabled = ref(false)

  const emailError = ref('')

  const formValue = ref({
    email: props.email || '',
  })

  const formRef = ref()

  const rules = {
    email: {
      required: true,
      asyncValidator: async (_rule: unknown, value: string) => {
        emailError.value = ''

        if (!value) {
          emailError.value = t('auth.validation.emailRequired')
          return Promise.reject(new Error(t('auth.validation.emailRequired')))
        }

        try {
          await yup.string().email(t('auth.validation.emailInvalid')).required(t('auth.validation.emailRequired')).validate(value)
          return Promise.resolve()
        } catch {
          emailError.value = t('auth.validation.emailInvalid')
          return Promise.reject(new Error(emailError.value))
        }
      },
      trigger: ['input', 'blur'],
    },
  }

  const handleValidateClick = async (formRefInstance: AuthForgotPasswordFormRef): Promise<void> => {
    loading.value = true
    errorMessage.value = null
    await formRefInstance.getForm().restoreValidation()
    const result = await validateForm(formRefInstance)
    if (!result) {
      loading.value = false
      return
    }
    // Defensive check to ensure email exists before trimming
    if (!formValue.value?.email) {
      console.error('Email is undefined after validation')
      loading.value = false
      return
    }

    try {
      const auth = useAuth()
      await auth.migrateEmail(formValue.value.email.trim())

      const { data } = await auth.forgotPassword(formValue.value.email.trim(), reCaptchaToken.value)

      if (data?.success) {
        loading.value = false
        emit('changeScreen', {
          page: Screens.PROCEED_TO_LOGIN,
          email: formValue.value.email,
        })
      }
    } catch (error) {
      const err = error as { response?: { status?: number } }
      if (err.response?.status === 429) {
        resetPasswordDisabled.value = true
        showRecaptcha.value = true
      } else {
        showRecaptcha.value = false
        errorMessage.value = getErrorMessage(error as Error)
      }
      loading.value = false
    }
  }

  const onFormValueChange = () => {
    errorMessage.value = null
  }

  const onVerify = (response: string) => {
    reCaptchaToken.value = response
    resetPasswordDisabled.value = false
  }

  const onError = () => {
    resetPasswordDisabled.value = true
  }

  const onExpired = () => {
    reCaptchaToken.value = undefined
    resetPasswordDisabled.value = true
  }

  // Lifecycle hooks
  onMounted(() => {
    if (props.email) {
      formValue.value.email = props.email
    }
  })

  // Watch effects
  watch(
    () => props.email,
    (newEmail: string) => {
      if (newEmail) {
        formValue.value.email = newEmail
      }
    }
  )

  watch(
    () => formValue.value?.email,
    () => {
      emailError.value = ''
      errorMessage.value = null
    }
  )

  // User-defined functions
  async function validateForm(formRefInstance: AuthForgotPasswordFormRef): Promise<boolean> {
    try {
      await formRefInstance.getForm().validate()
      return true
    } catch (e) {
      console.error('error', e)
      return false
    }
  }
</script>

<template>
  <CPForm
    :id="createElementId('forgot-password', 'modal', 'form')"
    ref="formRef"
    class="flex flex-col items-center"
    :model="formValue"
    :rules="rules"
    size="sm"
    @keyup.enter="handleValidateClick(formRef as AuthForgotPasswordFormRef)"
  >
    <CPFormItem
      :id="createElementId('forgot-password', 'modal', 'email-form-item')"
      class="w-full"
      :label="t('auth.email')"
      required
      path="email"
      :show-require-mark="true"
      label-size="md"
    >
      <CPInput
        :id="createElementId('forgot-password', 'modal', 'email-input')"
        v-model="formValue.email"
        :placeholder="t('auth.emailPlaceholder')"
        size="lg"
        @input="onFormValueChange()"
      />
      <template #feedback>
        <!-- CPText → HLText does not put `id` on the DOM node; wrapper carries the E2E id. -->
        <div :id="createElementId('forgot-password', 'modal', 'password-reset-link-info')" class="w-full">
          <CPText size="sm" :class="emailError ? 'text-danger' : 'text-clientportal-font-secondary'">
            {{ emailError || t('auth.passwordResetLinkInfo') }}
          </CPText>
        </div>
      </template>
    </CPFormItem>

    <div v-if="errorMessage" :id="createElementId('forgot-password', 'modal', 'error-message')" class="text-danger w-full">
      <CPText size="sm">{{ errorMessage }}</CPText>
    </div>

    <Recaptcha
      v-if="showRecaptcha"
      :id="createElementId('forgot-password', 'modal', 'recaptcha')"
      class="flex w-full justify-center"
      @expired="onExpired"
      @verify="onVerify"
      @error="onError"
    />

    <CPButton
      :id="createElementId('forgot-password', 'modal', 'reset-button')"
      class="w-full rounded-xl shadow-sm mt-1"
      type="primary"
      size="lg"
      :loading="loading"
      :disabled="loading || resetPasswordDisabled"
      @click="handleValidateClick(formRef as AuthForgotPasswordFormRef)"
    >
      {{ t('auth.resetPasswordNow') }}
    </CPButton>

    <CPButton
      :id="createElementId('forgot-password', 'modal', 'login-button')"
      class="w-full mt-2"
      type="secondary"
      size="lg"
      @click="emit('changeScreen', { page: Screens.LOGIN })"
    >
      {{ t('auth.loginWithPassword') }}
    </CPButton>
  </CPForm>
</template>

<style scoped>
  .hr-form {
    gap: 16px;
  }
</style>
